<template>
  <v-skeleton-loader
    :loading="loading"
    type="table-thead, table-row-divider@10"
  >
    <v-data-table
      :items="tableData.items"
      :headers="tableData.headers"
      :search="tableState.search"
      :sort-by.sync="tableState.sortBy"
      :sort-desc.sync="tableState.sortDesc"
      :page.sync="tableState.page"
      :items-per-page.sync="tableState.itemsPerPage"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
    >
      <template #item="{ item }">
        <tr class="td-container">
          <td>
            {{ regionalView ? item.organizationPath : item.facilityName }}
          </td>
          <td>
            <TableCellWrapper :is-highlighted="highlightColumn.conversions">
              {{ thousandSeparator(item.subscriptionsAdded) }}
            </TableCellWrapper>
          </td>
          <td>
            <TableCellWrapper :is-highlighted="highlightColumn.conversions">
              {{ thousandSeparator(item.totalMoveIns) }}
            </TableCellWrapper>
          </td>
          <td>
            <TableCellWrapper :is-highlighted="highlightColumn.conversionRate">
              <div :style="{ color: getColorByPercentage(item.currentMonth) }">
                {{ item.currentMonth }}%
              </div>
            </TableCellWrapper>
          </td>
          <template v-if="selectedPeriod === 'byMonths'">
            <td v-for="month in tableData.months" :key="month">
              <TableCellWrapper
                :is-highlighted="highlightColumn.month === month"
                :value="item[month]"
              />
            </td>
          </template>
          <template v-else>
            <td v-for="month in tableData.byTrendsMonths" :key="month">
              <TableCellWrapper
                :is-highlighted="highlightColumn.month === month"
                :value="item[month]"
              />
            </td>
            <td>{{ item[ROLLING_AVG] }}%</td>
          </template>
        </tr>
      </template>
      <template #body.append>
        <tr class="table-total">
          <td>Total</td>
          <td>
            <TableCellWrapper :is-highlighted="highlightColumn.conversions">
              {{ thousandSeparator(totalAddedSubscriptions) }}
            </TableCellWrapper>
          </td>
          <td>
            <TableCellWrapper :is-highlighted="highlightColumn.conversions">
              {{ thousandSeparator(totalMoveIns) }}
            </TableCellWrapper>
          </td>
          <td>
            <TableCellWrapper :is-highlighted="highlightColumn.conversionRate">
              <div
                :style="{ color: getColorByPercentage(totalConversionRate) }"
              >
                {{ totalConversionRate }}%
              </div>
            </TableCellWrapper>
          </td>
          <td></td>
          <td></td>
          <td v-if="selectedPeriod === 'byTrends'">
            {{ totalRollingAverage }}%
          </td>
        </tr>
      </template>
      <template #footer.prepend>
        <ButtonExportCSV
          class="order-last"
          :table-data="tableData.items"
          :headers="tableData.headers"
          filename="monthly_trends"
        />
        <ButtonPrint
          class="order-last"
          :before-print="showAllItems"
          :after-print="restoreItemsPerPage"
        />
      </template>
    </v-data-table>
  </v-skeleton-loader>
</template>

<script>
import {
  formatMonthYear,
  getLast12Months,
  getMonth,
} from "@tod-ui/helpers/datetime";
import { MONTHLY_CONVERSIONS } from "./graphql";
import {
  thousandSeparator,
  toLowerCaseAndRemoveSpaces,
} from "@tod-ui/helpers/strings";
import { ROLLING_AVG } from "@tod-ui/constants/dashboard";
import {
  calculateComparisonPercentage,
  calculateConversionRate,
  getColorByPercentage,
  transformMonthlyTrendsData,
} from "../helper";
import TableCellWrapper from "@layout/TableCellWrapper.vue";
import { useRoute } from "vue-router/composables";
import { computed } from "vue";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import useDataTable from "@components/common/useDataTable";
import { calculateTotal, roundValue } from "@tod-ui/helpers/math";
import { groupByRegion } from "../helper";
import useUser from "@components/authentication/useUser";

export default {
  components: {
    TableCellWrapper,
    ButtonExportCSV,
    ButtonPrint,
  },
  props: {
    selectedFacilities: {
      type: Array,
      default: () => [],
    },
    selectedPeriod: {
      type: String,
      required: true,
    },
    regionalView: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const route = useRoute();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
      sortBy: ["id desc"],
    });

    const { defaultOrganization } = useUser();

    const highlightColumn = computed(() => {
      const query = route.query.highlight_column;
      return {
        conversionRate: ["conversions%(mtd)", "mtdconversion"].includes(query),
        conversions: query === "conversions",
        month: query,
      };
    });

    return {
      ROLLING_AVG,
      calculateComparisonPercentage,
      highlightColumn,
      tableState,
      showAllItems,
      restoreItemsPerPage,
      thousandSeparator,
      defaultOrganization,
    };
  },
  data() {
    return {
      monthlyConversions: [],
    };
  },
  computed: {
    previousMonth() {
      return getMonth(0, -1, this.defaultOrganization.timeZone);
    },
    previousYearSameMonth() {
      return getMonth(-1, 0, this.defaultOrganization.timeZone);
    },
    last12Months() {
      return getLast12Months(null, this.defaultOrganization.timeZone).slice(1);
    },
    last3Months() {
      return getLast12Months(null, this.defaultOrganization.timeZone).slice(
        0,
        3
      );
    },
    byTrendsMonths() {
      return [
        getMonth(0, -1, this.defaultOrganization.timeZone),
        getMonth(-1, 0, this.defaultOrganization.timeZone),
      ];
    },
    monthlyTrends() {
      return transformMonthlyTrendsData(
        this.monthlyConversions,
        "conversionRate",
        this.last12Months,
        "month",
        this.defaultOrganization.timeZone
      );
    },
    transformedMonthlyTrends() {
      return this.monthlyTrends.map((item) => {
        this.last12Months.forEach((month) => {
          const monthKey = toLowerCaseAndRemoveSpaces(
            formatMonthYear(month, this.defaultOrganization.timeZone)
          );
          item[monthKey] = calculateComparisonPercentage(item, monthKey);
        });
        return item;
      });
    },

    regionWiseReport() {
      const fieldsToSum = [
        "subscriptionsAdded",
        "totalMoveIns",
        ...this.last12Months.map((dateString) =>
          toLowerCaseAndRemoveSpaces(
            formatMonthYear(dateString, this.defaultOrganization.timeZone)
          )
        ),
      ];
      const avgFields = ["rollingaverage"];

      const groupedData = groupByRegion(
        this.monthlyTrends,
        fieldsToSum,
        avgFields
      );
      const calculateMonthlyOverallConversion = (data, monthDate) => {
        // Exact match on the region, not a substring match of the region name
        // against the full organization path: that mixed every region whose
        // name contained another's into one figure, and threw outright on a
        // facility with no path (which a directly-granted facility can have).
        const monthlyData = this.monthlyConversions.filter(
          (record) =>
            record.region === data.region && record.month === monthDate
        );
        if (!monthlyData.length) return 0;

        const totalSubscriptions = monthlyData.reduce(
          (sum, record) => sum + record.subscriptionsAdded,
          0
        );
        const totalMoveIns = monthlyData.reduce(
          (sum, record) => sum + record.totalMoveIns,
          0
        );

        return totalMoveIns ? (totalSubscriptions / totalMoveIns) * 100 : 0;
      };

      // Perform additional calculations
      return groupedData.map((item) => {
        item.currentMonth = item.totalMoveIns
          ? calculateConversionRate(item.subscriptionsAdded, item.totalMoveIns)
          : 0;

        this.last12Months.forEach((month) => {
          const monthKey = toLowerCaseAndRemoveSpaces(
            formatMonthYear(month, this.defaultOrganization.timeZone)
          );
          const monthValue = calculateMonthlyOverallConversion(item, month);

          item[monthKey] =
            monthValue !== 0
              ? roundValue(
                  ((item.currentMonth - monthValue) / monthValue) * 100
                )
              : 0;
        });
        const lastMonth = calculateMonthlyOverallConversion(
          item,
          this.last3Months[1]
        );
        const twoMonthsAgo = calculateMonthlyOverallConversion(
          item,
          this.last3Months[2]
        );

        item.rollingaverage = roundValue(
          (lastMonth + twoMonthsAgo + item.currentMonth) / 3
        );

        return item;
      });
    },

    totalAddedSubscriptions() {
      return calculateTotal(this.monthlyTrends, "subscriptionsAdded");
    },
    totalRollingAverage() {
      if (
        !Array.isArray(this.monthlyTrends) ||
        this.monthlyTrends.length === 0 ||
        !ROLLING_AVG
      ) {
        return 0;
      }

      let sum = 0;
      let count = 0;
      this.monthlyTrends.forEach((item) => {
        const value = parseFloat(item[ROLLING_AVG]);
        if (!isNaN(value)) {
          sum += value;
          count += 1;
        }
      });
      return count === 0 ? 0 : roundValue(sum / count);
    },
    totalMoveIns() {
      return calculateTotal(this.monthlyTrends, "totalMoveIns");
    },
    totalConversionRate() {
      return calculateConversionRate(
        this.totalAddedSubscriptions,
        this.totalMoveIns
      );
    },
    loading() {
      return this.$apollo.queries.monthlyConversions.loading;
    },
    tableData() {
      const baseHeaders = [
        this.regionalView
          ? { text: "Organization / Region", value: "organizationPath" }
          : { text: "Facility Name", value: "facilityName" },
        { text: "Added Subscriptions (MTD)", value: "subscriptionsAdded" },
        { text: "Total Move-Ins (MTD)", value: "totalMoveIns" },
        { text: "Conversion %(MTD)", value: "currentMonth" },
      ];

      const headers = [...baseHeaders];

      if (this.selectedPeriod === "byMonths") {
        headers.push(...this.generateMonthlyHeaders(this.last12Months));
      } else {
        headers.push(...this.generateByTrendsHeaders());
      }
      return {
        headers,
        items: this.regionalView
          ? this.regionWiseReport
          : this.transformedMonthlyTrends,
        tableMonths: this.last12Months,
        months: this.last12Months.map((month) =>
          toLowerCaseAndRemoveSpaces(
            formatMonthYear(month, this.defaultOrganization.timeZone)
          )
        ),
        byTrendsMonths: this.byTrendsMonths.map((month) =>
          toLowerCaseAndRemoveSpaces(
            formatMonthYear(month, this.defaultOrganization.timeZone)
          )
        ),
      };
    },
  },
  apollo: {
    monthlyConversions: {
      query: MONTHLY_CONVERSIONS,
      variables() {
        return {
          ids: this.selectedFacilities.map((f) => f.id),
          month: getLast12Months(null, this.defaultOrganization.timeZone),
        };
      },
      skip() {
        return this.selectedFacilities.length === 0;
      },
    },
  },
  methods: {
    generateMonthlyHeaders(tableMonths) {
      const monthsHeaders = tableMonths.map((month) => ({
        text: `.vs ${formatMonthYear(
          month,
          this.defaultOrganization.timeZone
        )} (%)`,
        value: toLowerCaseAndRemoveSpaces(
          formatMonthYear(month, this.defaultOrganization.timeZone)
        ),
      }));

      return monthsHeaders;
    },
    generateByTrendsHeaders() {
      return [
        {
          text: `vs ${formatMonthYear(
            this.previousMonth,
            this.defaultOrganization.timeZone
          )} (%)`,
          value: toLowerCaseAndRemoveSpaces(
            formatMonthYear(
              this.previousMonth,
              this.defaultOrganization.timeZone
            )
          ),
        },
        {
          text: `vs ${formatMonthYear(
            this.previousYearSameMonth,
            this.defaultOrganization.timeZone
          )} (%)`,
          value: toLowerCaseAndRemoveSpaces(
            formatMonthYear(
              this.previousYearSameMonth,
              this.defaultOrganization.timeZone
            )
          ),
        },
        {
          text: "3-Month Rolling Average (%)",
          value: ROLLING_AVG,
        },
      ];
    },
    getColorByPercentage,
  },
};
</script>

<style scoped>
.td-container td {
  min-width: 205px;
}
</style>
